<template>
<div>
  <div class="d-flex flex-column flex-gap-2 mb-3">
    <v-select
      class="v-select-filter"
      placeholder="Location.."
      label="zcCity"
      v-model="selectedLocations"
      :filterable="false"
      :options="locations"
      :reduce="zc => ({id: zc.id, zcZip: zc.zcZip, zcCity: zc.zcCity})"
      @search="onSearchLocation"
      @input="inputFilter"
      multiple
    >
      <template v-slot:option="zip_code">
        <strong>{{ zip_code.zcZip }}</strong> -
        <span>{{ zip_code.zcCity }} </span>
      </template>
      <template slot="selected-option" slot-scope="zip_code">
        <strong class="text-dark mr-1">{{ zip_code.zcZip }}</strong>
        <span>{{ zip_code.zcCity }} </span>
      </template>
      <span slot="no-options">Type to get locations..</span>
    </v-select>

    <CInput
      class="mb-0 search"
      type="search"
      placeholder="Search for restaurants..."
      v-model="search"
      @input="searchRestaurants"
    />

    <CInputCheckbox
      custom
      class="mb-0"
      label="Show inactive restaurants"
      :checked.sync="includeNoneActive"
      @update:checked="inputFilter"
    />
  </div>

  <CSpinner v-if="loading" />

  <CDataTable
    v-else-if="restaurants.length"
    class="table-add-delivery-service"
    striped
    hover
    :items="restaurants"
    :fields="fields"
    :noItemsView="{
      noResults: 'No filtering results are available!',
      noItems: 'No restaurants found!',
    }"
    clickable-rows
    @row-clicked="rowClicked"
  >
    <!-- Check Box -->
    <template #select="{item}">
      <td>
        <CInputCheckbox
          :checked="item._selected"
          :disabled="item.has_delivery_service"
          @update:checked="() => check(item)"
          custom
        />
      </td>
    </template>

    <!-- ID -->
    <template #id="{item}">
      <td>
        #{{ item?.id }}
      </td>
    </template>

    <!-- Logo -->
    <template #restaurant_logo="{item}">
      <td>
        <CImg
          :src="item.restaurant_logo_path"
          class="c-avatar-img"
          style="width: 36px; height: 36px"
          placeholderColor="lightgray"
        />
      </td>
    </template>

    <!-- Name -->
    <template #restaurant_name="{item}">
      <td>
        {{ item.restaurant_name }} <span v-if="item.none_active">(NONE_ACTIVE!)</span>
        <CBadge v-if="item.has_delivery_service" color="secondary" class="ml-2">Already added</CBadge>
      </td>
    </template>

    <!-- Location -->
    <template #location="{item}">
      <td>
        <strong>{{item.restaurant_city }}</strong> <small>{{item.restaurant_postcode }}</small>
      </td>
    </template>
  </CDataTable>
</div>
</template>

<script>
import _debounce from 'lodash/debounce';

export default {
  name: "AddRestaurant",
  props: {
    active: { default: false, type: Boolean },
    selectedItems: { default: () => [], type: Array },
  },
  data() {
    return {
      fields: [
        { key: "select", label: "", sorter: false, _style: "width: 1%" },
        { key: "id", label: "ID", _style: "min-width: 96px; width: 96px;" },
        { key: "restaurant_logo", label: "", sorter: false,  _style: "min-width: 60px; width: 60px;" },
        { key: "restaurant_name", _style: "min-width: 200px;" },
        { key: "location", _style : "min-width: 150px;" },
      ],

      restaurants: [],
      search: null,
      locations: [],
      selectedLocations: [],
      includeNoneActive: false,

      loading: false,
    };
  },

  watch: {
    active: async function (val) {
      if (val) {
        this.search = null;
        this.locations = [];
        this.selectedLocations = [];
        this.includeNoneActive = false;
        this.restaurants = [];
        this.$emit("update:selectedItems", []);
      }
    },
  },

  beforeMount() {
    this.searchRestaurants = _debounce(() => {
      if (this.canFetchRestaurants()) {
        this.getRestaurantsWithoutDS();
      } else {
        this.restaurants = [];
      }
    }, 1000);
  },

  methods: {
    canFetchRestaurants() {
      return (this.search && this.search.length >= 3) || this.selectedLocations.length > 0;
    },
    async getRestaurantsWithoutDS() {
      if (!this.canFetchRestaurants()) {
        this.restaurants = [];
        return;
      }

      this.loading = true;
      await this.$axios
        .get(this.$backend.COURIER.DELIVERY_SERVICE.RESTAURANTS_WITHOUT_DS,
          {
            params: {
              search: this.search,
              country: this.$store.state.filters.country,
              fields: 'none_active',
              locations: this.selectedLocations.map(z => z.zcZip).join(','),
              'include-delivery-service': true,
              'include-none-active': this.includeNoneActive,
            }
          })
        .then(({ data }) => {
          this.restaurants = data.map((el) => {
            return {
              ...el,
              _selected: false,
              _classes: el.has_delivery_service ? 'table-secondary already-added' : '',
            }
          });
        })
        .catch(() => this.restaurants = [])
        .finally(() => this.loading = false)
    },
    onSearchLocation(search, loading) {
      if (search.length) {
        loading(true);
        this.getLocations(loading, search, this);
      }
    },
    getLocations: _debounce((loading, search, vm) => {
      vm.$axios.get(vm.$backend.ZIP_CODES.SEARCH, {
        params: {
          country: vm.$store.state.filters.country,
          fields: 'zcZip,zcCity',
          q: search,
        },
      })
        .then((response) => (vm.locations = response.data))
        .finally(() => { loading(false); });
    }, 1000),
    inputFilter() {
      this.getRestaurantsWithoutDS();
    },

    rowClicked(item, index, column, e) {
      if (item.has_delivery_service) {
        return;
      }

      // INPUT, LABEL ve BUTTON dışında herhangi bir satır nesnesine tıklanırsa;
      if (!['INPUT', 'LABEL', 'BUTTON', 'A'].includes(e.target.tagName)) {
        this.check(item)
      }
    },
    check(item) {
      if (item.has_delivery_service) {
        return;
      }

      this.$set(item, "_selected", !item._selected);

      if (item._selected) {
        item._classes = item._classes.concat(" table-selected");
        this.selectedItems.push(item);
      } else {
        item._classes = item._classes.replace(" table-selected", "");
        this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
      }
    },
  },
};
</script>

<style>
.table-add-delivery-service table {
  margin-bottom: 0;
}
.table-add-delivery-service th,
.table-add-delivery-service td {
  padding: 0.5rem 0.5rem;
  vertical-align: middle;
}
.table-add-delivery-service th {
  border-top: none;
}
.table-add-delivery-service tr.already-added {
  cursor: not-allowed;
  opacity: 0.75;
}
</style>
